import React, { useState, useEffect } from "react";

import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    MobileStepper, Button, Typography, useTheme, Grid, FormControl, TextField
} from "@material-ui/core";

import Rating from '@material-ui/lab/Rating';

import { KeyboardArrowRight, KeyboardArrowLeft, Done, Undo } from "@material-ui/icons";

import memberService from "../../../domain/services/solicitation/member.service";
import solicitationService from "../../../domain/services/solicitation/solicitation.service";

const DecisionOption = ({ Icon, title, description, onClick, disabled = false, primary = false }) => (
    <Button
        fullWidth
        variant={primary ? "contained" : "outlined"}
        color="primary"
        disabled={disabled}
        disableElevation
        onClick={onClick}
        style={{ justifyContent: "flex-start", textTransform: "none", padding: "12px 16px", marginTop: 12 }}
    >
        <Icon style={{ marginRight: 14, flexShrink: 0 }} />
        <span style={{ textAlign: "left" }}>
            <Typography component="span" style={{ display: "block", fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
            <Typography component="span" variant="body2" style={{ display: "block", opacity: 0.85, lineHeight: 1.35 }}>{description}</Typography>
        </span>
    </Button>
);

const ReviewerDecisionSection = ({ feedback, onApprove, onReturn }) => {

    const hasFeedback = Boolean(feedback.trim());

    return (
        <section style={{ marginTop: 24 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Qual o desfecho da revisão?</Typography>
            <DecisionOption Icon={Done} primary title="Aprovar e concluir"
                description="A peça está pronta. A revisão é concluída e a peça segue para o cliente."
                onClick={onApprove} />
            <DecisionOption Icon={Undo} title="Devolver para ajustes"
                description="A peça volta ao redator para corrigir, junto com os apontamentos do seu feedback."
                onClick={onReturn} disabled={!hasFeedback} />
            {!hasFeedback && (
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
                    Preencha o feedback com seus apontamentos para liberar a devolução ao redator.
                </Typography>
            )}
        </section>
    );
};

const RatingForm = (props) => {

    const {
        requestId, questions, setQuestions, saveResultQuestion, isModalOpen, concluded,
        closeModal, name, dontShowFaciliterName = false, feedbackLabel = 'Feedback', showReviewForAllSituations = false,
        returnToWriter, showReviewerDecision = false
    } = props;

    const theme = useTheme();

    const [members, setMembers] = useState([]);
    const [feedback, setFeedback] = useState("");

    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        let isSubscribed = true;

        if (isSubscribed)
            findMembers();

        return () => (isSubscribed = false);
    }, [requestId])

    const findMembers = async () => {

        const { data } = await memberService.find(requestId)
            .catch(err => ({ data: [] }));

        setMembers(data);

        return data;
    }

    const setQuestionAnswer = (e, index) => {
        let item = [...questions];
        item[index].answer = parseInt(e.target.value);
        setQuestions(item);
        handleNext();
    }

    const handleNext = () => {
        if (!questions[activeStep].answer)
            return;

        saveResultQuestion({ ...questions[activeStep] }, members);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }

    const getStepperOptions = (totalQuestions) => (
        <MobileStepper variant="dots" steps={totalQuestions + 1} position="static"
            activeStep={activeStep} sx={{ flexGrow: 1 }}
            style={{ width: "100%", background: "unset" }}
            nextButton={
                <section>
                    <Button size="small" onClick={handleNext} disabled={activeStep === totalQuestions}>
                        Continuar {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                    </Button>
                </section>
            }
            backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                    {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />} Voltar
                </Button>
            }
        />
    );

    const getQuestion = (index) => {
        let question = questions[index];
        if (!question)
            return getFeedbackOption();

        return (
            <section style={{ textAlign: "center", marginTop: "10px" }}>
                <Typography component="h4" style={{ fontWeight: 450, fontSize: "16px", width: "100%" }}>{question.description}</Typography>
                <Rating name={`rating-${question.id}`} max={5} style={{ marginTop: "10px" }}
                    value={(question.answer || 0)} onChange={(e) => { setQuestionAnswer(e, index) }} />
                <span style={{ display: "block", color: "rgb(0 0 0 / 44%)", fontWeight: "600" }}>* Informe a nota para continuar</span>
            </section>
        );
    }

    const getFeedbackOption = () => {
        return (
            <Grid item md={12} style={{ marginTop: "10px" }}>
                <FormControl fullWidth my={2} variant="outlined">
                    <TextField name="feedback" label={feedbackLabel} multiline={true}
                        placeholder="Deixe aqui seu feedback..." variant="outlined" value={feedback}
                        onChange={(e) => { setFeedback(e.target.value); }}
                    />
                    <span style={{ display: "block", color: "rgb(0 0 0 / 44%)", fontWeight: "600" }}>* Informe o feedback para concluir</span>
                </FormControl>
            </Grid>
        );
    }

    if (!showReviewForAllSituations && !solicitationService.canReviewSolicitation())
        return null;

    const isFinalStep = activeStep === questions.length;

    return (
        <React.Fragment>
            <Dialog open={isModalOpen} onClose={closeModal} maxWidth={showReviewerDecision ? "sm" : "xs"} fullWidth disableBackdropClick disableEscapeKeyDown>
                <DialogTitle style={{ paddingBottom: "10px" }}>{name}</DialogTitle>
                {
                    (questions.length || showReviewerDecision) ?
                        <DialogContent>
                            <section style={{ marginTop: 0, marginBottom: "10px", width: "100%" }}>
                                {members.length && !dontShowFaciliterName ? <span style={{ color: "rgb(0 0 0 / 60%)", fontWeight: 500 }}>Faciliter: {members.map(item => item.name).join(", ")}</span> : null}
                            </section>
                            {getQuestion(activeStep)}
                            {showReviewerDecision && isFinalStep && (
                                <ReviewerDecisionSection
                                    feedback={feedback}
                                    onApprove={() => concluded(feedback, members, closeModal)}
                                    onReturn={() => returnToWriter(feedback, members, closeModal)}
                                />
                            )}
                            {questions.length ? getStepperOptions(questions.length) : null}
                        </DialogContent> : null
                }
                <DialogActions>
                    <Button size="small" variant="contained" onClick={closeModal}>Cancelar</Button>
                    {!showReviewerDecision && (
                        <Button size="small" disabled={!isFinalStep} variant="contained" color="primary" onClick={() => concluded(feedback, members, closeModal)}>Concluir</Button>
                    )}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default RatingForm;


